import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, type Messaging } from 'firebase/messaging';
import api from './api';
import { useNotificationsStore } from '../stores/notifications';

/**
 * Web Push / PWA notification support via Firebase Cloud Messaging.
 *
 * Runs ONLY in browsers / PWAs (native Capacitor apps use @capacitor/push-notifications
 * and the Firebase SDK/APNs bridge on the native side). All config comes from
 * VITE_FIREBASE_* env vars — nothing is hardcoded.
 */

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;
let currentToken: string | null = null;

export function isWebPushConfigured(): boolean {
  return !!(firebaseConfig.apiKey && firebaseConfig.projectId && VAPID_KEY);
}

export function isWebPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function buildSwUrl(): string {
  const params = new URLSearchParams();
  params.set('apiKey', firebaseConfig.apiKey || '');
  params.set('authDomain', firebaseConfig.authDomain || '');
  params.set('projectId', firebaseConfig.projectId || '');
  params.set('storageBucket', firebaseConfig.storageBucket || '');
  params.set('messagingSenderId', firebaseConfig.messagingSenderId || '');
  params.set('appId', firebaseConfig.appId || '');
  return `/firebase-messaging-sw.js?${params.toString()}`;
}

async function ensureServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register(buildSwUrl(), {
      scope: '/firebase-cloud-messaging-push-scope',
    });
    // Wait for it to become active so getToken doesn't race the install.
    if (registration.installing) {
      await new Promise<void>((resolve) => {
        const sw = registration.installing!;
        sw.addEventListener('statechange', () => {
          if (sw.state === 'activated') resolve();
        });
      });
    }
    return registration;
  } catch {
    return null;
  }
}

/**
 * Request permission and register this browser/PWA for web push.
 * Safe to call multiple times — no-ops if already registered or unsupported.
 */
export async function initWebPushNotifications(): Promise<void> {
  if (!isWebPushSupported() || !isWebPushConfigured()) return;

  try {
    const permission =
      Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();
    if (permission !== 'granted') return;

    const swRegistration = await ensureServiceWorker();
    if (!swRegistration) return;

    if (!app) app = initializeApp(firebaseConfig);
    if (!messaging) messaging = getMessaging(app);

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swRegistration,
    });

    if (!token || token === currentToken) return;

    await api.post('/auth/fcm-token', { token, platform: 'web' });
    currentToken = token;

    // Foreground messages — browser doesn't show a native banner, surface via toast.
    onMessage(messaging, (payload) => {
      try {
        const store = useNotificationsStore();
        store.addNotification({
          id: Date.now(),
          type: (payload.data?.type as string) || 'general',
          title: payload.notification?.title || '',
          message: payload.notification?.body || '',
          data: payload.data as Record<string, unknown>,
          readAt: null,
          createdAt: new Date().toISOString(),
        });
      } catch {
        // Non-critical
      }
    });
  } catch {
    // Non-critical — app works without push
  }
}

/**
 * Clean up the registered token on logout so the backend stops pushing to
 * this browser for the previous user.
 */
export async function deregisterWebPushNotifications(): Promise<void> {
  if (!currentToken) return;
  try {
    await api.delete('/auth/fcm-token', { data: { token: currentToken } });
  } catch {
    // Non-critical
  }
  currentToken = null;
}
